import { OperationResult, OperationStatus } from '../types';
import ApiError from '../types/error';

export interface DevicesInitialState {
  deviceCategories: DeviceCategory[];
  getDeviceCategories: OperationResult;
  postDeviceMappingRequest: OperationResult;
  getDeviceMappingRequest: OperationResult;
  deviceMappingRequest: Record<string, OperationResult>;
  devices: Record<string, Device[] | string | ApiError> | null;
  deviceMetadata: Record<string, DeviceMetadata[]> | null;
}

export const initialState: DevicesInitialState = {
  getDeviceCategories: {
    status: OperationStatus.NOT_STARTED,
    error: null
  },
  postDeviceMappingRequest: {
    status: OperationStatus.NOT_STARTED,
    error: null
  },
  getDeviceMappingRequest: {
    status: OperationStatus.NOT_STARTED,
    error: null
  },
  deviceMappingRequest: {},
  deviceCategories: [],
  devices: null,
  deviceMetadata: null
};

export interface DeviceCategory {
  category_type: string;
  category_description: string;
  device_type: string;
  device_sub_type?: string;
}

export interface DoNotSeeDevicePropsType {
  children?: React.ReactNode;
}

export enum CategoryType {
  HOME_AND_OFFICE_PRINTER = 'home-and-office-printer',
  PERSONAL_SYSTEMS = 'personal-systems',
  PAGEWIDE_WEB_PRESS = 'pagewide-web-press',
  INDIGO_DIGITAL_PRESS = 'indigo-digital-press',
  SCITEX = 'scitex',
  LARGE_FORMAT = 'large-format-printer',
  THREE_D_PRINT = '3d-printer'
}

export enum DeviceType {
  PRINTER = 'printer',
  THREE_D = '3d',
  PERSONAL_COMPUTER = 'personal-computer',
  INDUSTRIAL = 'industrial',
  LARGE_FORMAT = 'large-format'
}

export enum DeviceSubType {
  HOME_PRINTER = 'home-printer',
  HOME_OFFICE_AND_BUSINESS_PRINTER = 'home-office-and-business-printer',
  WEB_PRESS = 'pagewide-web-press',
  DIGITAL_PRESS = 'indigo-digital-press',
  SCITEX = 'scitex'
}

export enum DeviceMappingRequestStatus {
  QUEUED = 'QUEUED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

export enum TelemetryFormat {
  CDM = 'cdm',
  LEDM = 'ledm',
  AMPV = 'ampv'
}

export enum DeviceGrantPolicy {
  EXPIRES_AT = 'expires_at'
}

export enum DeviceGrantPolicyValueType {
  DATE_TIME = 'date-time'
}

export enum DeviceSharingStatus {
  SHARED_BY_ME = 'shared-by-me',
  SHARED_TO_ME = 'shared-to-me'
}

export interface Device {
  device_image_url?: string;
  device_type: DeviceType;
  device_sub_type?: DeviceSubType;
  device_serial_nr: string;
  device_id?: string;
  device_uuid?: string;
  device_model?: string;
  device_family?: string;
  device_telemetry_format: TelemetryFormat;
}

interface DeviceMappingRequest {
  id: string;
  created_at: string;
  updated_at?: string;
  type: string;
  status: DeviceMappingRequestStatus;
  status_reason?: string;
  trace_id?: string;
}

export interface GetDeviceMappingRequestPayload {
  device_type: DeviceType;
  device_sub_type?: DeviceSubType;
  category_type?: CategoryType;
}

export interface DeviceMetadata {
  device_serial_nr: string;
  status: DeviceSharingStatus;
}

export interface GetDeviceMappingRequestResponse {
  id: string;
  created_at: string;
  updated_at?: string;
  type: string;
  status: DeviceMappingRequestStatus;
  status_reason?: string;
  devices: Device[];
  DeviceMetadata?: DeviceMetadata[];
  trace_id?: string;
  maxAge?: number | null;
}
export interface PostDeviceMappingRequestResponse extends DeviceMappingRequest {
  id: string;
  created_at: string;
  updated_at?: string;
  type: string;
  status: DeviceMappingRequestStatus;
  status_reason?: string;
  device_type: DeviceType;
  device_sub_type?: DeviceSubType;
  trace_id?: string;
}

export interface PostDeviceMappingRequestPayload {
  device_type: DeviceType;
  device_sub_type?: DeviceSubType;
}

export interface PostDeviceSharingRequestPayload {
  user: { email: string };
  device: Device;
}

export interface FilterDeviceSharingRequestsPayload {
  email: 'headers.x-user-hp-id';
}

export interface DeviceSharingPolicyItem {
  device_grant_policy: DeviceGrantPolicy;
  device_grant_policy_value_type: DeviceGrantPolicyValueType;
  device_grant_policy_value: string;
}

export interface PostDeviceSharingRequestResponse {
  id: string;
  type: string;
  user: { email: string };
  device: Device;
  policies: DeviceSharingPolicyItem[];
  trace_id: string;
  created_at: string;
  statusCode?: number;
}

export interface DeviceSharingRequest {
  id: string;
  type: string;
  user: { email: string };
  device: Device;
  policies: DeviceSharingPolicyItem[];
  trace_id: string;
}

export type GetDeviceSharingRequestsResponse = DeviceSharingRequest[];

export enum DeviceSharingRequestAction {
  EXTEND = 'EXTEND',
  REVOKE = 'REVOKE'
}

export interface PostDeviceSharingActionPayload {
  id: string;
  action: DeviceSharingRequestAction;
  email: string; // stored for toast display, NOT sent to API
}

export interface PostDeviceSharingActionResponse {
  id: string;
  policies: DeviceSharingPolicyItem[];
}
